import {
  useMemo,
  useState,
} from 'react';

import type {
  MouseEvent,
} from 'react';

import {
  Box,
  Button,
  ButtonBase,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import CalendarMonthRoundedIcon
  from '@mui/icons-material/CalendarMonthRounded';
import CompareArrowsRoundedIcon
  from '@mui/icons-material/CompareArrowsRounded';
import ExpandMoreRoundedIcon
  from '@mui/icons-material/ExpandMoreRounded';

import {
  format,
  isAfter,
  parseISO,
  subDays,
} from 'date-fns';

import {
  useSearchParams,
} from 'react-router-dom';

import ProaDatePicker
  from '../../../components/Common/ProaDatePicker';

import type {
  MarginCenterComparisonMode,
  MarginCenterTimeGrain,
} from '../types/marginCenter';


type TemporalPanel =
  | 'period'
  | 'comparison'
  | 'grain';


interface TemporalSelectionLike {
  start_date: string | null;
  end_date: string | null;

  comparison_mode: MarginCenterComparisonMode;
  comparison_start_date: string | null;
  comparison_end_date: string | null;

  time_grain: MarginCenterTimeGrain;
}


interface TemporalDatasetLike {
  first_transaction_date: string | null;
  last_transaction_date: string | null;
}


interface TemporalDraft {
  startDate: string;
  endDate: string;

  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate: string;
  comparisonEndDate: string;

  timeGrain: MarginCenterTimeGrain;
}


const COMPARISONS: Array<{
  value: MarginCenterComparisonMode;
  label: string;
}> = [
  {
    value: 'previous_period',
    label: 'Periodo anterior',
  },
  {
    value: 'year_over_year',
    label: 'Mismo periodo año anterior',
  },
  {
    value: 'custom',
    label: 'Personalizada',
  },
  {
    value: 'none',
    label: 'Sin comparación',
  },
];


const GRAINS: Array<{
  value: MarginCenterTimeGrain;
  label: string;
}> = [
  { value: 'day', label: 'Día' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
];


const PRESETS: Array<{
  label: string;
  days: number | null;
}> = [
  { label: 'Últimos 30 días', days: 30 },
  { label: 'Últimos 90 días', days: 90 },
  { label: 'Últimos 180 días', days: 180 },
  { label: 'Último año', days: 365 },
  { label: 'Todo el periodo disponible', days: null },
];


const EXPLICIT_TEMPORAL_KEYS = [
  'from',
  'to',
  'comparison',
  'comparisonFrom',
  'comparisonTo',
] as const;


export function hasExplicitMarginCenterTemporalContext(
  searchParams: URLSearchParams,
): boolean {
  return EXPLICIT_TEMPORAL_KEYS.some(
    (key) => searchParams.has(key),
  );
}


export function buildRecommendedMarginCenterTemporalSearchParams(
  searchParams: URLSearchParams,
  lastTransactionDate: string | null | undefined,
): URLSearchParams | null {
  if (
    hasExplicitMarginCenterTemporalContext(searchParams)
    || !lastTransactionDate
  ) {
    return null;
  }

  const endDate =
    lastTransactionDate.slice(0, 10);

  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(endDate)
  ) {
    return null;
  }

  const year =
    endDate.slice(0, 4);

  const next =
    new URLSearchParams(searchParams);

  next.set(
    'from',
    `${year}-01-01`,
  );

  next.set(
    'to',
    endDate,
  );

  next.set(
    'comparison',
    'year_over_year',
  );

  if (!next.has('grain')) {
    next.set(
      'grain',
      'week',
    );
  }

  return next;
}


function formatDisplayDate(
  value: string | null | undefined,
): string {
  if (!value) return '—';

  const normalized = value.slice(0, 10);
  const [year, month, day] = normalized
    .split('-')
    .map(Number);

  if (!year || !month || !day) return normalized;

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}


function buildDraft(
  selection: TemporalSelectionLike,
): TemporalDraft {
  return {
    startDate: selection.start_date ?? '',
    endDate: selection.end_date ?? '',
    comparisonMode: selection.comparison_mode,
    comparisonStartDate:
      selection.comparison_start_date ?? '',
    comparisonEndDate:
      selection.comparison_end_date ?? '',
    timeGrain: selection.time_grain,
  };
}


function getComparisonLabel(
  selection: TemporalSelectionLike,
): string {
  if (selection.comparison_mode === 'none') {
    return 'Sin comparación';
  }

  if (selection.comparison_mode === 'previous_period') {
    return 'Periodo anterior';
  }

  if (selection.comparison_mode === 'year_over_year') {
    return 'Mismo periodo año anterior';
  }

  if (
    selection.comparison_start_date
    && selection.comparison_end_date
  ) {
    return `${formatDisplayDate(
      selection.comparison_start_date,
    )} – ${formatDisplayDate(
      selection.comparison_end_date,
    )}`;
  }

  return 'Comparación personalizada';
}


function getGrainLabel(
  grain: MarginCenterTimeGrain,
): string {
  return GRAINS.find(
    (item) => item.value === grain,
  )?.label ?? grain;
}


export default function MarginCenterTemporalContextBar({
  selection,
  dataset,
  compact = false,
  normalizeSearchParams,
}: {
  selection: TemporalSelectionLike;
  dataset: TemporalDatasetLike;
  compact?: boolean;

  normalizeSearchParams?: (
    next: URLSearchParams,
  ) => void;
}) {
  const [searchParams, setSearchParams] =
    useSearchParams();

  const [anchorEl, setAnchorEl] =
    useState<HTMLElement | null>(null);

  const [activePanel, setActivePanel] =
    useState<TemporalPanel | null>(null);

  const [draft, setDraft] =
    useState<TemporalDraft>(() =>
      buildDraft(selection)
    );

  const [validationError, setValidationError] =
    useState<string | null>(null);

  const periodLabel = useMemo(
    () =>
      `${formatDisplayDate(
        selection.start_date,
      )} – ${formatDisplayDate(
        selection.end_date,
      )}`,
    [
      selection.end_date,
      selection.start_date,
    ],
  );

  const comparisonLabel = useMemo(
    () => getComparisonLabel(selection),
    [selection],
  );

  const comparisonPeriodLabel = useMemo(
    () => {
      if (
        selection.comparison_mode === 'none'
        || !selection.comparison_start_date
        || !selection.comparison_end_date
      ) {
        return null;
      }

      return `${formatDisplayDate(
        selection.comparison_start_date,
      )} – ${formatDisplayDate(
        selection.comparison_end_date,
      )}`;
    },
    [
      selection.comparison_mode,
      selection.comparison_start_date,
      selection.comparison_end_date,
    ],
  );

  const open =
    Boolean(anchorEl)
    && activePanel !== null;

  const handleOpen = (
    event: MouseEvent<HTMLElement>,
    panel: TemporalPanel,
  ) => {
    setDraft(buildDraft(selection));
    setValidationError(null);
    setActivePanel(panel);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActivePanel(null);
    setValidationError(null);
  };

  const commitSearchParams = (
    update: (next: URLSearchParams) => void,
  ) => {
    const next =
      new URLSearchParams(searchParams);

    update(next);

    normalizeSearchParams?.(next);

    next.delete('cursor');
    next.delete('relationshipCursor');
    next.delete('productCustomerCursor');

    setSearchParams(
      next,
      { replace: true },
    );

    handleClose();
  };

  const applyPreset = (
    days: number | null,
  ) => {
    const latest =
      dataset.last_transaction_date
      ?? selection.end_date;

    if (!latest) {
      setValidationError(
        'No hay una fecha máxima disponible para aplicar este periodo.',
      );
      return;
    }

    if (days === null) {
      const first =
        dataset.first_transaction_date;

      if (!first) {
        setValidationError(
          'No hay una fecha inicial disponible para usar todo el periodo disponible.',
        );
        return;
      }

      setDraft((current) => ({
        ...current,
        startDate: first,
        endDate: latest,
      }));

      setValidationError(null);
      return;
    }

    const presetEnd =
      parseISO(latest);

    const presetStart =
      subDays(
        presetEnd,
        days - 1,
      );

    setDraft((current) => ({
      ...current,
      startDate: format(
        presetStart,
        'yyyy-MM-dd',
      ),
      endDate: format(
        presetEnd,
        'yyyy-MM-dd',
      ),
    }));

    setValidationError(null);
  };

  const applyPeriod = () => {
    if (
      !draft.startDate
      || !draft.endDate
    ) {
      setValidationError(
        'Debes indicar la fecha inicial y final del periodo.',
      );
      return;
    }

    if (
      isAfter(
        parseISO(draft.startDate),
        parseISO(draft.endDate),
      )
    ) {
      setValidationError(
        'La fecha inicial no puede ser posterior a la fecha final.',
      );
      return;
    }

    commitSearchParams((next) => {
      next.set(
        'from',
        draft.startDate,
      );
      next.set(
        'to',
        draft.endDate,
      );
    });
  };

  const applyComparison = () => {
    if (
      draft.comparisonMode === 'custom'
    ) {
      if (
        !draft.comparisonStartDate
        || !draft.comparisonEndDate
      ) {
        setValidationError(
          'La comparación personalizada necesita ambas fechas.',
        );
        return;
      }

      if (
        isAfter(
          parseISO(
            draft.comparisonStartDate,
          ),
          parseISO(
            draft.comparisonEndDate,
          ),
        )
      ) {
        setValidationError(
          'La fecha inicial de comparación no puede ser posterior a la final.',
        );
        return;
      }
    }

    commitSearchParams((next) => {
      next.set(
        'comparison',
        draft.comparisonMode,
      );

      if (
        draft.comparisonMode === 'custom'
      ) {
        next.set(
          'comparisonFrom',
          draft.comparisonStartDate,
        );
        next.set(
          'comparisonTo',
          draft.comparisonEndDate,
        );
      } else {
        next.delete('comparisonFrom');
        next.delete('comparisonTo');
      }
    });
  };

  const applyGrain = (
    grain: MarginCenterTimeGrain,
  ) => {
    commitSearchParams((next) => {
      next.set(
        'grain',
        grain,
      );
    });
  };

  const popoverWidth =
    activePanel === 'period'
      ? 620
      : activePanel === 'comparison'
        ? 480
        : 300;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          mt: compact ? 0 : 1.25,
          display: 'inline-flex',
          maxWidth: '100%',
          borderRadius: compact ? 1.75 : 2.25,
          border:
            '1px solid rgba(148,163,184,0.16)',
          bgcolor: compact
            ? 'rgba(7,14,28,0.52)'
            : 'rgba(15,23,42,0.42)',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          alignItems="stretch"
          sx={{
            minWidth: 0,
            overflowX: 'auto',
          }}
        >
          <ButtonBase
            onClick={(event) =>
              handleOpen(
                event,
                'period',
              )
            }
            aria-haspopup="dialog"
            aria-expanded={
              activePanel === 'period'
              && open
            }
            sx={{
              px: 1.4,
              py: compact ? 0.65 : 0.85,
              minWidth: 0,
              textAlign: 'left',
              justifyContent: 'flex-start',
              '&:hover': {
                bgcolor:
                  'rgba(56,189,248,0.06)',
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <CalendarMonthRoundedIcon
                sx={{
                  fontSize: 18,
                  color: '#64748b',
                }}
              />

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: '#64748b',
                    fontWeight: 750,
                    lineHeight: 1.1,
                  }}
                >
                  Periodo analizado
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.25,
                    color: '#e2e8f0',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {periodLabel}
                </Typography>
              </Box>

              <ExpandMoreRoundedIcon
                sx={{
                  fontSize: 17,
                  color: '#64748b',
                }}
              />
            </Stack>
          </ButtonBase>

          <Divider
            orientation="vertical"
            flexItem
          />

          <ButtonBase
            onClick={(event) =>
              handleOpen(
                event,
                'comparison',
              )
            }
            aria-haspopup="dialog"
            aria-expanded={
              activePanel === 'comparison'
              && open
            }
            sx={{
              px: 1.4,
              py: 0.85,
              minWidth: 0,
              textAlign: 'left',
              justifyContent: 'flex-start',
              '&:hover': {
                bgcolor:
                  'rgba(56,189,248,0.06)',
              },
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <CompareArrowsRoundedIcon
                sx={{
                  fontSize: 18,
                  color: '#64748b',
                }}
              />

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: '#64748b',
                    fontWeight: 750,
                    lineHeight: 1.1,
                  }}
                >
                  Comparación
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.25,
                    color: '#e2e8f0',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {comparisonLabel}
                </Typography>

                {comparisonPeriodLabel && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: '#64748b',
                      mt: 0.15,
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {comparisonPeriodLabel}
                  </Typography>
                )}
              </Box>

              <ExpandMoreRoundedIcon
                sx={{
                  fontSize: 17,
                  color: '#64748b',
                }}
              />
            </Stack>
          </ButtonBase>

          <Divider
            orientation="vertical"
            flexItem
          />

          <ButtonBase
            onClick={(event) =>
              handleOpen(
                event,
                'grain',
              )
            }
            aria-haspopup="dialog"
            aria-expanded={
              activePanel === 'grain'
              && open
            }
            sx={{
              px: 1.4,
              py: 0.85,
              textAlign: 'left',
              justifyContent: 'flex-start',
              '&:hover': {
                bgcolor:
                  'rgba(56,189,248,0.06)',
              },
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: '#64748b',
                  fontWeight: 750,
                  lineHeight: 1.1,
                }}
              >
                Granularidad
              </Typography>

              <Stack
                direction="row"
                spacing={0.4}
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.25,
                    color: '#e2e8f0',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getGrainLabel(
                    selection.time_grain,
                  )}
                </Typography>

                <ExpandMoreRoundedIcon
                  sx={{
                    mt: 0.25,
                    fontSize: 17,
                    color: '#64748b',
                  }}
                />
              </Stack>
            </Box>
          </ButtonBase>
        </Stack>
      </Paper>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: {
                xs:
                  'calc(100vw - 32px)',
                sm: popoverWidth,
              },
              maxWidth:
                'calc(100vw - 32px)',
              borderRadius: 3,
              border:
                '1px solid rgba(148,163,184,0.18)',
              bgcolor: '#0f172a',
              backgroundImage: 'none',
            },
          },
        }}
      >
        {activePanel === 'period' && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 850,
                  }}
                >
                  Periodo analizado
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.25,
                    color: '#94a3b8',
                  }}
                >
                  Define el intervalo que gobierna
                  KPIs, cambios y posicionamiento.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#94a3b8',
                    fontWeight: 800,
                  }}
                >
                  Accesos rápidos
                </Typography>

                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mt: 0.75 }}
                >
                  {PRESETS.map((preset) => (
                    <Button
                      key={preset.label}
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        applyPreset(
                          preset.days,
                        )
                      }
                      sx={{
                        textTransform: 'none',
                      }}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </Stack>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                  },
                  gap: 1.25,
                }}
              >
                <ProaDatePicker
                  label="Desde"
                  value={draft.startDate}
                  onChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      startDate: value,
                    }))
                  }
                  minDate={
                    dataset.first_transaction_date
                  }
                  maxDate={
                    dataset.last_transaction_date
                  }
                />

                <ProaDatePicker
                  label="Hasta"
                  value={draft.endDate}
                  onChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      endDate: value,
                    }))
                  }
                  minDate={
                    dataset.first_transaction_date
                  }
                  maxDate={
                    dataset.last_transaction_date
                  }
                />
              </Box>

              {validationError && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fca5a5',
                  }}
                >
                  {validationError}
                </Typography>
              )}

              <Divider />

              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
              >
                <Button
                  onClick={handleClose}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Cancelar
                </Button>

                <Button
                  variant="contained"
                  onClick={applyPeriod}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Aplicar periodo
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {activePanel === 'comparison' && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 850,
                  }}
                >
                  Comparación
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.25,
                    color: '#94a3b8',
                  }}
                >
                  Elige únicamente cómo quieres
                  comparar el periodo analizado.
                </Typography>
              </Box>

              <FormControl
                size="small"
                fullWidth
              >
                <InputLabel id="margin-center-comparison-label">
                  Comparar con
                </InputLabel>

                <Select
                  labelId="margin-center-comparison-label"
                  label="Comparar con"
                  value={draft.comparisonMode}
                  onChange={(event) => {
                    const nextComparison =
                      event.target.value as MarginCenterComparisonMode;

                    setDraft((current) => ({
                      ...current,
                      comparisonMode:
                        nextComparison,
                    }));

                    setValidationError(null);
                  }}
                >
                  {COMPARISONS.map(
                    (comparison) => (
                      <MenuItem
                        key={comparison.value}
                        value={comparison.value}
                      >
                        {comparison.label}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>

              {draft.comparisonMode === 'custom' && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr 1fr',
                    },
                    gap: 1.25,
                  }}
                >
                  <ProaDatePicker
                    label="Desde"
                    value={
                      draft.comparisonStartDate
                    }
                    onChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        comparisonStartDate:
                          value,
                      }))
                    }
                    minDate={
                      dataset.first_transaction_date
                    }
                    maxDate={
                      dataset.last_transaction_date
                    }
                  />

                  <ProaDatePicker
                    label="Hasta"
                    value={
                      draft.comparisonEndDate
                    }
                    onChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        comparisonEndDate:
                          value,
                      }))
                    }
                    minDate={
                      dataset.first_transaction_date
                    }
                    maxDate={
                      dataset.last_transaction_date
                    }
                  />
                </Box>
              )}

              {validationError && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fca5a5',
                  }}
                >
                  {validationError}
                </Typography>
              )}

              <Divider />

              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
              >
                <Button
                  onClick={handleClose}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Cancelar
                </Button>

                <Button
                  variant="contained"
                  onClick={applyComparison}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Aplicar comparación
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {activePanel === 'grain' && (
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 850,
                  }}
                >
                  Granularidad
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.25,
                    color: '#94a3b8',
                  }}
                >
                  Cambia únicamente el detalle
                  temporal de las series.
                </Typography>
              </Box>

              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={
                  selection.time_grain
                }
                onChange={(
                  _,
                  nextGrain:
                    MarginCenterTimeGrain | null,
                ) => {
                  if (!nextGrain) {
                    return;
                  }

                  applyGrain(nextGrain);
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    color: '#94a3b8',
                    borderColor:
                      'rgba(148,163,184,0.2)',
                    '&.Mui-selected': {
                      color: '#f8fafc',
                      bgcolor:
                        'rgba(56,189,248,0.12)',
                    },
                  },
                }}
              >
                {GRAINS.map((grain) => (
                  <ToggleButton
                    key={grain.value}
                    value={grain.value}
                  >
                    {grain.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          </Box>
        )}
      </Popover>
    </>
  );
}
